import React, { useRef, useEffect, useCallback, useImperativeHandle, forwardRef, useState } from 'react';
import jsQR from 'jsqr';
import { performOCR } from '../../services/visionAPI';
import { WebcamViewport, type WebcamViewportHandle } from './WebcamViewport';
import { QR_MISS_FADE_MS, QR_NOT_DETECTED_MESSAGE } from './qrScanMessages';
import './WebcamCapture.css';

interface CaptureProps {
  onCapture: (data: string | null) => void;
  overlay?: React.ReactNode;
  /** Control pinned to the top-right corner of the preview (e.g. close camera). */
  cornerAction?: React.ReactNode;
  /** QR mode only. When true, the viewfinder does not draw the detection crosshairs. */
  hideReticle?: boolean;
}

export interface WebcamCaptureHandle {
  capture: () => void;
  flash: () => void;
}

interface QrCornerPoint {
  x: number;
  y: number;
}

interface QrOverlayLocation {
  topLeftCorner: QrCornerPoint;
  topRightCorner: QrCornerPoint;
  bottomRightCorner: QrCornerPoint;
  bottomLeftCorner: QrCornerPoint;
}

export const WebcamCapture = forwardRef<WebcamCaptureHandle, CaptureProps>(
  function WebcamCapture({ onCapture, overlay, cornerAction }, ref): React.ReactElement {
    const viewportRef = useRef<WebcamViewportHandle>(null);

    const handleCapture = useCallback((): void => {
      const frame = viewportRef.current?.getVisibleFrame();
      if (!frame) return;
      onCapture(frame.toJpegDataUrl());
    }, [onCapture]);

    const handlePreviewClick = (event: React.MouseEvent<HTMLDivElement>): void => {
      event.stopPropagation();
      handleCapture();
    };

    useImperativeHandle(
      ref,
      () => ({
        capture: handleCapture,
        flash: (): void => {
          viewportRef.current?.flash();
        },
      }),
      [handleCapture],
    );

    return (
      <WebcamViewport
        ref={viewportRef}
        overlay={overlay}
        cornerAction={cornerAction}
        onClick={handlePreviewClick}
      />
    );
  },
);

export const WebcamCaptureHybrid = forwardRef<WebcamCaptureHandle, CaptureProps>(
  function WebcamCaptureHybrid({ onCapture, overlay, cornerAction }, ref): React.ReactElement {
    const viewportRef = useRef<WebcamViewportHandle>(null);
    const overlayRef = useRef<HTMLCanvasElement>(null);
    const lastQrDataRef = useRef<string | null>(null);
    const lastQrOverlayLocationRef = useRef<QrOverlayLocation | null>(null);
    const lastQrSeenAtRef = useRef(0);
    const qrMissFramesRef = useRef(0);
    const lastQrTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      let rafId: number | null = null;

      const tick = (): void => {
        const overlay = overlayRef.current;
        const frame = viewportRef.current?.getVisibleFrame();
        if (!overlay || !frame) {
          rafId = requestAnimationFrame(tick);
          return;
        }

        const { imageData, width: scanW, height: scanH } = frame;
        const code = jsQR(imageData.data, scanW, scanH);

        if (code && code.data) {
          lastQrDataRef.current = code.data;
          lastQrSeenAtRef.current = Date.now();
          qrMissFramesRef.current = 0;
          if (lastQrTimeoutRef.current) clearTimeout(lastQrTimeoutRef.current);
          lastQrTimeoutRef.current = setTimeout(() => {
            lastQrDataRef.current = null;
            lastQrOverlayLocationRef.current = null;
            qrMissFramesRef.current = 0;
            lastQrTimeoutRef.current = null;
          }, LAST_QR_HOLD_MS);
        } else {
          qrMissFramesRef.current += 1;
        }

        const ow = overlay.clientWidth;
        const oh = overlay.clientHeight;
        if (!ow || !oh) {
          rafId = requestAnimationFrame(tick);
          return;
        }

        const overlayCtx = overlay.getContext('2d');
        if (!overlayCtx) {
          rafId = requestAnimationFrame(tick);
          return;
        }
        overlay.width = ow;
        overlay.height = oh;
        overlayCtx.clearRect(0, 0, ow, oh);

        const scaleX = ow / scanW;
        const scaleY = oh / scanH;
        if (code && code.location) {
          lastQrOverlayLocationRef.current = toNormalizedOverlayLocation(
            code.location,
            scaleX,
            scaleY,
            ow,
            oh,
          );
        } else if (
          Date.now() - lastQrSeenAtRef.current > LAST_QR_RETICLE_HOLD_MS ||
          qrMissFramesRef.current >= MAX_RETICLE_MISS_FRAMES
        ) {
          lastQrOverlayLocationRef.current = null;
        }

        if (lastQrOverlayLocationRef.current) {
          drawReticleFromNormalizedLocation(overlayCtx, lastQrOverlayLocationRef.current, ow, oh);
        }

        rafId = requestAnimationFrame(tick);
      };

      rafId = requestAnimationFrame(tick);
      return () => {
        if (rafId) cancelAnimationFrame(rafId);
        if (lastQrTimeoutRef.current) clearTimeout(lastQrTimeoutRef.current);
      };
    }, []);

    const handleCapture = useCallback(async (): Promise<void> => {
      const frame = viewportRef.current?.getVisibleFrame();
      if (!frame) return;

      const code = jsQR(frame.imageData.data, frame.width, frame.height);
      if (code) {
        onCapture(code.data);
        return;
      }
      if (lastQrDataRef.current) {
        onCapture(lastQrDataRef.current);
        return;
      }

      const ocrText = await performOCR(frame.toJpegDataUrl());
      if (ocrText && ocrText.trim()) {
        onCapture(ocrText);
      } else {
        onCapture(null);
      }
    }, [onCapture]);

    useImperativeHandle(
      ref,
      () => ({
        capture: (): void => {
          void handleCapture();
        },
        flash: (): void => {
          viewportRef.current?.flash();
        },
      }),
      [handleCapture],
    );

    return (
      <WebcamViewport
        ref={viewportRef}
        className="webcam-wrapper-qr"
        overlay={overlay}
        cornerAction={cornerAction}
        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
          event.stopPropagation();
          void handleCapture();
        }}
      >
        <canvas ref={overlayRef} className="webcam-qr-overlay" aria-hidden="true" />
      </WebcamViewport>
    );
  },
);

function drawReticle(
  ctx: CanvasRenderingContext2D,
  location: QrOverlayLocation | null,
  scaleX: number,
  scaleY: number,
): void {
  if (!location) return;
  const { topLeftCorner, topRightCorner, bottomRightCorner, bottomLeftCorner } = location;

  const scale = (p: QrCornerPoint): QrCornerPoint => ({ x: p.x * scaleX, y: p.y * scaleY });
  const tl = scale(topLeftCorner);
  const tr = scale(topRightCorner);
  const br = scale(bottomRightCorner);
  const bl = scale(bottomLeftCorner);

  const cx = (tl.x + tr.x + br.x + bl.x) / 4;
  const cy = (tl.y + tr.y + br.y + bl.y) / 4;

  const armLen = 32;
  const circleRadius = 8;

  ctx.save();
  ctx.strokeStyle = '#f00';
  ctx.fillStyle = '#f00';
  ctx.lineWidth = 4;
  ctx.lineCap = 'round';

  ctx.beginPath();
  ctx.moveTo(cx - armLen, cy);
  ctx.lineTo(cx + armLen, cy);
  ctx.moveTo(cx, cy - armLen);
  ctx.lineTo(cx, cy + armLen);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, circleRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  ctx.restore();
}

function toNormalizedOverlayLocation(
  location: QrOverlayLocation,
  scaleX: number,
  scaleY: number,
  overlayWidth: number,
  overlayHeight: number,
): QrOverlayLocation {
  const norm = (p: QrCornerPoint): QrCornerPoint => ({
    x: (p.x * scaleX) / overlayWidth,
    y: (p.y * scaleY) / overlayHeight,
  });
  return {
    topLeftCorner: norm(location.topLeftCorner),
    topRightCorner: norm(location.topRightCorner),
    bottomRightCorner: norm(location.bottomRightCorner),
    bottomLeftCorner: norm(location.bottomLeftCorner),
  };
}

function drawReticleFromNormalizedLocation(
  ctx: CanvasRenderingContext2D,
  normalizedLocation: QrOverlayLocation,
  overlayWidth: number,
  overlayHeight: number,
): void {
  const denorm = (p: QrCornerPoint): QrCornerPoint => ({
    x: p.x * overlayWidth,
    y: p.y * overlayHeight,
  });
  drawReticle(
    ctx,
    {
      topLeftCorner: denorm(normalizedLocation.topLeftCorner),
      topRightCorner: denorm(normalizedLocation.topRightCorner),
      bottomRightCorner: denorm(normalizedLocation.bottomRightCorner),
      bottomLeftCorner: denorm(normalizedLocation.bottomLeftCorner),
    },
    1,
    1,
  );
}

const DECODE_INTERVAL_MS = 150;
const LAST_QR_HOLD_MS = 1500;
const LAST_QR_RETICLE_HOLD_MS = 250;
const MAX_RETICLE_MISS_FRAMES = 6;

export const WebcamCaptureQR = forwardRef<WebcamCaptureHandle, CaptureProps>(
  function WebcamCaptureQR({ onCapture, overlay, cornerAction, hideReticle = false }, ref): React.ReactElement {
    const viewportRef = useRef<WebcamViewportHandle>(null);
    const overlayRef = useRef<HTMLCanvasElement>(null);
    const lastQrDataRef = useRef<string | null>(null);
    const lastQrOverlayLocationRef = useRef<QrOverlayLocation | null>(null);
    const lastQrSeenAtRef = useRef(0);
    const qrMissFramesRef = useRef(0);
    const lastQrTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const decodeMissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hideReticleRef = useRef(hideReticle);
    hideReticleRef.current = hideReticle;
    const [decodeMissKey, setDecodeMissKey] = useState<number | null>(null);

    useEffect(() => {
      return () => {
        if (decodeMissTimerRef.current) clearTimeout(decodeMissTimerRef.current);
      };
    }, []);

    useEffect(() => {
      let rafId: number | null = null;
      let lastDecodeTime = 0;

      const tick = (): void => {
        const overlay = overlayRef.current;
        if (!overlay) {
          rafId = requestAnimationFrame(tick);
          return;
        }

        const now = performance.now();
        if (now - lastDecodeTime >= DECODE_INTERVAL_MS) {
          lastDecodeTime = now;
          const frame = viewportRef.current?.getVisibleFrame();
          if (frame) {
            const code = jsQR(frame.imageData.data, frame.width, frame.height);

            if (code && code.data) {
              lastQrDataRef.current = code.data;
              lastQrSeenAtRef.current = Date.now();
              qrMissFramesRef.current = 0;
              if (lastQrTimeoutRef.current) clearTimeout(lastQrTimeoutRef.current);
              lastQrTimeoutRef.current = setTimeout(() => {
                lastQrDataRef.current = null;
                lastQrOverlayLocationRef.current = null;
                qrMissFramesRef.current = 0;
                lastQrTimeoutRef.current = null;
              }, LAST_QR_HOLD_MS);

              const ow = overlay.clientWidth;
              const oh = overlay.clientHeight;
              if (!hideReticleRef.current && ow && oh && code.location) {
                lastQrOverlayLocationRef.current = toNormalizedOverlayLocation(
                  code.location,
                  ow / frame.width,
                  oh / frame.height,
                  ow,
                  oh,
                );
              }
            } else {
              qrMissFramesRef.current += 1;
              if (
                Date.now() - lastQrSeenAtRef.current > LAST_QR_RETICLE_HOLD_MS ||
                qrMissFramesRef.current >= MAX_RETICLE_MISS_FRAMES
              ) {
                lastQrOverlayLocationRef.current = null;
              }
            }
          }
        }

        const ow = overlay.clientWidth;
        const oh = overlay.clientHeight;
        if (ow && oh) {
          const overlayCtx = overlay.getContext('2d');
          if (overlayCtx) {
            overlay.width = ow;
            overlay.height = oh;
            overlayCtx.clearRect(0, 0, ow, oh);

            if (hideReticleRef.current) {
              lastQrOverlayLocationRef.current = null;
            } else if (lastQrOverlayLocationRef.current) {
              drawReticleFromNormalizedLocation(overlayCtx, lastQrOverlayLocationRef.current, ow, oh);
            }
          }
        }

        rafId = requestAnimationFrame(tick);
      };

      rafId = requestAnimationFrame(tick);
      return () => {
        if (rafId) cancelAnimationFrame(rafId);
        if (lastQrTimeoutRef.current) clearTimeout(lastQrTimeoutRef.current);
      };
    }, []);

    const showDecodeMiss = useCallback((): void => {
      if (decodeMissTimerRef.current) clearTimeout(decodeMissTimerRef.current);
      setDecodeMissKey((key) => (key ?? 0) + 1);
      decodeMissTimerRef.current = setTimeout(() => {
        decodeMissTimerRef.current = null;
        setDecodeMissKey(null);
      }, QR_MISS_FADE_MS);
    }, []);

    const handleCapture = useCallback((): void => {
      const frame = viewportRef.current?.getVisibleFrame();
      if (!frame) return;
      const code = jsQR(frame.imageData.data, frame.width, frame.height);

      if (code) {
        onCapture(code.data);
      } else if (lastQrDataRef.current) {
        onCapture(lastQrDataRef.current);
      } else {
        showDecodeMiss();
      }
    }, [onCapture, showDecodeMiss]);

    useImperativeHandle(
      ref,
      () => ({
        capture: handleCapture,
        flash: (): void => {
          viewportRef.current?.flash();
        },
      }),
      [handleCapture],
    );

    return (
      <WebcamViewport
        ref={viewportRef}
        className="webcam-wrapper-qr"
        overlay={overlay}
        cornerAction={cornerAction}
        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
          event.stopPropagation();
          handleCapture();
        }}
      >
        <canvas ref={overlayRef} className="webcam-qr-overlay" aria-hidden="true" />
        {decodeMissKey != null ? (
          <div className="webcam-qr-miss-banner" aria-live="polite">
            <div key={decodeMissKey} className="webcam-qr-miss-error" role="status">
              {QR_NOT_DETECTED_MESSAGE}
            </div>
          </div>
        ) : null}
      </WebcamViewport>
    );
  },
);
